/**
 * 软导航顶栏细进度条：NProgress 式左→右填充（trickle → complete）。
 *
 * Callers: AppProviders（须在 NavigationPendingProvider 内）
 */

import { useEffect, useState } from "react";

import { useNavigationPending } from "./navigation-pending";

/** 起始进度（与经典 NProgress minimum 对齐）。 */
const INITIAL_PROGRESS = 0.08;
/** 软天花板：pending 期间不越过此值。 */
const TRICKLE_CEILING = 0.9;
/** trickle 间隔。 */
export const NAVIGATION_PROGRESS_TRICKLE_MS = 200;
/** 完成到 100% 后收起延迟。 */
export const NAVIGATION_PROGRESS_FINISH_MS = 250;

type ProgressPhase = {
  finishing: boolean;
  pending: boolean;
  progress: number;
};

const idlePhase: ProgressPhase = {
  finishing: false,
  pending: false,
  progress: 0,
};

/** NProgress 式递减步进：越接近天花板步长越小。 */
const nextTrickleProgress = (current: number): number => {
  if (current >= TRICKLE_CEILING) return current;
  const remaining = TRICKLE_CEILING - current;
  const step =
    current < 0.2
      ? 0.1
      : current < 0.5
        ? 0.04
        : current < 0.8
          ? 0.02
          : remaining * 0.05;
  return Math.min(TRICKLE_CEILING, current + step);
};

/** 跟随 pending：左→右 trickle 至 ~90%，settle 后填满再收起；减少动效时隐藏。 */
export function NavigationProgress() {
  const { isSoftNavPending } = useNavigationPending();
  const [reduceMotion, setReduceMotion] = useState(false);
  const [phase, setPhase] = useState<ProgressPhase>(idlePhase);

  // Adjust local phase when soft-nav pending flips (preferred over sync setState-in-effect).
  if (isSoftNavPending && !phase.pending) {
    setPhase({
      finishing: false,
      pending: true,
      progress: INITIAL_PROGRESS,
    });
  } else if (!isSoftNavPending && phase.pending) {
    setPhase({
      finishing: true,
      pending: false,
      progress: 1,
    });
  }

  useEffect(() => {
    const media = window.matchMedia("(prefers-reduced-motion: reduce)");
    const sync = () => setReduceMotion(media.matches);
    sync();
    media.addEventListener("change", sync);
    return () => media.removeEventListener("change", sync);
  }, []);

  useEffect(() => {
    if (reduceMotion || !phase.pending) return;

    const trickleTimer = setInterval(() => {
      setPhase((current) => {
        if (!current.pending) return current;
        return {
          ...current,
          progress: nextTrickleProgress(current.progress),
        };
      });
    }, NAVIGATION_PROGRESS_TRICKLE_MS);

    return () => {
      clearInterval(trickleTimer);
    };
  }, [phase.pending, reduceMotion]);

  useEffect(() => {
    if (reduceMotion || !phase.finishing) return;

    const finishTimer = setTimeout(() => {
      setPhase(idlePhase);
    }, NAVIGATION_PROGRESS_FINISH_MS);

    return () => {
      clearTimeout(finishTimer);
    };
  }, [phase.finishing, reduceMotion]);

  if (reduceMotion || (!phase.pending && !phase.finishing)) {
    return null;
  }

  return (
    <div
      aria-hidden
      className="navigation-progress"
      data-progress={phase.progress.toFixed(2)}
      data-testid="navigation-progress"
    >
      <div
        className="navigation-progress__bar"
        style={{ transform: `scaleX(${phase.progress})` }}
      />
    </div>
  );
}
